import { useEffect, useMemo, useState } from 'react';
import { getReminderChannels, getReminderCourses } from '../../../api/reminders';
import { getDiscounts } from '../../../api/discounts';
import { useI18n } from '../../../i18n/I18nContext';
import { unwrapList } from '../../../utils/api';
import { PROMO_REDEEMABLE, isUsablePromo, promoLabel } from '../reminderUi';

/**
 * What a message form offers besides its text, read once when the form opens:
 *   channels  GET /crm/reminders/channels — undefined while loading, null when it could not be read
 *             (then no channel is blocked: the server skips the ones it cannot send)
 *   courses   [{ id, title }] for "which course the link opens" (GET /crm/reminders/courses, the
 *             business's published courses); [] when the list cannot be read
 *             (the field is then left out, as in the lead form)
 *   promos    [{ id, code, label }] — only the codes that work right now; [] hides the field. Always []
 *             (and the discounts are not read) while PROMO_REDEEMABLE is false: a code in a message
 *             cannot be redeemed at checkout yet
 * The two optional lists never fail the form: a login that cannot read them just lacks the field.
 */
export default function useComposerOptions() {
  const { lang } = useI18n();
  const [channels, setChannels] = useState(undefined);
  const [courses, setCourses] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getReminderChannels()
      .then((data) => { if (!cancelled) setChannels(data && typeof data === 'object' ? data : null); })
      .catch(() => { if (!cancelled) setChannels(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getReminderCourses()
      .then((list) => { if (!cancelled) setCourses(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!PROMO_REDEEMABLE) return undefined;
    let cancelled = false;
    getDiscounts({ limit: 100 })
      .then((res) => { if (!cancelled) setDiscounts(unwrapList(res)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const courseList = useMemo(
    () => courses.map((course) => ({ id: course.id, title: course.title })),
    [courses],
  );
  const promos = useMemo(
    () => (PROMO_REDEEMABLE
      ? discounts
        .filter((discount) => isUsablePromo(discount))
        .map((discount) => ({ id: discount.id, code: discount.code, label: promoLabel(discount, lang) }))
      : []),
    [discounts, lang],
  );

  return { channels, courses: courseList, promos };
}
